import DOMPurify from 'dompurify';
import { useId, type ChangeEvent } from 'react';

import { errorMessage, type FieldErrorLike } from '@/types/form';

export interface RadioOption {
    value: string;
    label: string;
}

/** `N` est le nom du champ : le `setValue` appelant doit accepter une chaîne pour ce champ. */
export interface RadioGroupProps<N extends string = string> {
    options: RadioOption[];
    setValue: (name: N, value: string, options?: { shouldValidate?: boolean }) => void;
    name: N;
    /** Peut contenir du HTML de mise en forme (assaini avant insertion). */
    label?: string;
    value?: string | null;
    required?: boolean;
    error?: FieldErrorLike;
    className?: string;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

/** Choix exclusif présenté en boutons. */
const RadioGroup = <N extends string>(props: RadioGroupProps<N>) => {
    const { options, setValue, name, label, value, required, error, className, onChange } = props;
    const id = useId();

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onChange?.(e);
        setValue(name, e.target.value, { shouldValidate: true });
    };

    return (
        <fieldset>
            <legend className="block text-sm font-medium mb-2">
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(label ?? '') }} />
                {required ? (
                    <sup className="text-destructive ml-0.5" aria-label="obligatoire">
                        *
                    </sup>
                ) : (
                    ''
                )}
            </legend>
            <div className={`flex flex-wrap gap-2 ${className || ''}`}>
                {options.map((option) => {
                    const radioId = `${id}-${name}-${option.value}`;
                    const isSelected = value === option.value;
                    return (
                        <label
                            key={option.value}
                            htmlFor={radioId}
                            className={`relative flex-1 min-w-[calc(50%-0.25rem)] sm:min-w-0 cursor-pointer select-none`}
                        >
                            <input
                                type="radio"
                                id={radioId}
                                name={name}
                                value={option.value}
                                checked={isSelected}
                                onChange={handleChange}
                                className="sr-only peer"
                            />
                            <span
                                className={`flex items-center justify-center px-6 py-2.5 rounded-lg border text-sm font-medium transition-all duration-200
 ${
                                    isSelected
                                        ? 'bg-primary text-white border-primary shadow-sm'
                                        : 'bg-card text-foreground border-border hover:border-primary/50 hover:bg-primary/5'
                                }`}
                            >
                                {option.label}
                            </span>
                        </label>
                    );
                })}
            </div>
            {error && (
                <p className="text-xs text-destructive italic mt-1" role="alert">
                    {errorMessage(error)}
                </p>
            )}
        </fieldset>
    );
};

export default RadioGroup;
